/** @jsxImportSource @emotion/react */
import React, { useRef, useState } from "react";
import { css } from "@emotion/react";
import MediaManager from "../MediaManager";
import { uploadStorageFile } from "../../../services/storage.service";

type MediaFieldProps = {
  label?: string;
  value?: string;
  folder?: string;
  onChange: (url: string) => void;
};

const root = css`
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 24px;
`;

const labelStyle = css`
  font-size: 11px;
  line-height: 14px;
  text-transform: uppercase;
  font-weight: 900;
  color: #0f172a;
  letter-spacing: 0.04em;
`;

const tabs = css`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const tab = (active: boolean) => css`
  border: 1px solid ${active ? "#34189a" : "#d8dee9"};
  background: ${active ? "#34189a" : "#ffffff"};
  color: ${active ? "#ffffff" : "#001740"};
  border-radius: 999px;
  padding: 9px 16px;
  font-size: 13px;
  font-weight: 900;
  cursor: pointer;
`;

const panel = css`
  border: 1px solid #e6e9f2;
  border-radius: 18px;
  background: #ffffff;
  padding: 18px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.04);
`;

const uploadArea = css`
  border: 1.5px dashed #b7a7ff;
  border-radius: 16px;
  background: #faf8ff;
  padding: 22px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const input = css`
  border: 1px solid #d8dee9;
  border-radius: 12px;
  padding: 13px 14px;
  font-size: 14px;
  width: 100%;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #34189a;
    box-shadow: 0 0 0 4px rgba(52, 24, 154, 0.08);
  }
`;

const hiddenFile = css`
  display: none;
`;

const fileRow = css`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const chooseButton = css`
  border: 1px solid #34189a;
  border-radius: 12px;
  background: #ffffff;
  color: #34189a;
  padding: 11px 16px;
  font-weight: 900;
  cursor: pointer;
`;

const fileName = css`
  font-size: 13px;
  color: #475569;
`;

const button = css`
  border: 0;
  border-radius: 12px;
  background: #34189a;
  color: white;
  padding: 12px 18px;
  font-weight: 900;
  cursor: pointer;
  width: fit-content;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const previewCard = css`
  border: 1px solid #e6e9f2;
  border-radius: 18px;
  overflow: hidden;
  background: #ffffff;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.04);
`;

const preview = css`
  min-height: 220px;
  background: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: center;

  img,
  video {
    width: 100%;
    max-height: 360px;
    object-fit: contain;
  }
`;

const urlBox = css`
  padding: 14px;
  font-size: 12px;
  color: #475569;
  word-break: break-all;
`;

const MediaField: React.FC<MediaFieldProps> = ({
  label = "Imagen",
  value,
  folder = "banners",
  onChange,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [mode, setMode] = useState<"upload" | "storage" | "url">("upload");
  const [file, setFile] = useState<File | null>(null);
  const [customName, setCustomName] = useState("");
  const [manualUrl, setManualUrl] = useState(value || "");
  const [loading, setLoading] = useState(false);

  const isImage = value && /\.(png|jpg|jpeg|svg|webp|gif)$/i.test(value);
  const isVideo = value && /\.(mp4|webm|mov)$/i.test(value);

  const handleUpload = async () => {
    if (!file) return;

    setLoading(true);

    try {
      const asset = await uploadStorageFile({
        file,
        folder,
        name: customName,
      });

      onChange(asset.url);
      setFile(null);
      setCustomName("");
    } catch (error) {
      console.error("[MEDIA_FIELD_UPLOAD_ERROR]", error);
      alert("No se pudo subir el archivo");
    } finally {
      setLoading(false);
    }
  };

  const handleManualUrl = () => {
    if (!manualUrl) return;
    onChange(manualUrl);
  };

  const handleCopy = async () => {
    if (!value) return;
    await navigator.clipboard.writeText(value);
    alert("URL copiada");
  };

  return (
    <div css={root}>
      <div css={labelStyle}>{label}</div>

      <div css={tabs}>
        <button type="button" css={tab(mode === "upload")} onClick={() => setMode("upload")}>
          Subir archivo
        </button>
        <button type="button" css={tab(mode === "storage")} onClick={() => setMode("storage")}>
          Elegir desde storage
        </button>
        <button type="button" css={tab(mode === "url")} onClick={() => setMode("url")}>
          Pegar URL
        </button>
      </div>

      {mode === "upload" && (
        <div css={panel}>
          <div css={uploadArea}>
            <input
              css={input}
              value={customName}
              onChange={(e) => setCustomName(e.target.value)}
              placeholder="Nombre opcional: banner-home-es"
            />

            <div css={fileRow}>
              <input
                ref={fileInputRef}
                css={hiddenFile}
                type="file"
                accept="image/*,video/*,.svg,.pdf"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />

              <button
                type="button"
                css={chooseButton}
                onClick={() => fileInputRef.current?.click()}
              >
                Seleccionar archivo
              </button>

              <span css={fileName}>{file ? file.name : "Ningún archivo seleccionado"}</span>
            </div>

            <button type="button" css={button} disabled={!file || loading} onClick={handleUpload}>
              {loading ? "Subiendo..." : "Subir y usar"}
            </button>
          </div>
        </div>
      )}

      {mode === "storage" && (
        <div css={panel}>
          <MediaManager initialFolder={folder} onSelect={(asset) => onChange(asset.url)} />
        </div>
      )}

      {mode === "url" && (
        <div css={panel}>
          <div css={uploadArea}>
            <input
              css={input}
              value={manualUrl}
              onChange={(e) => setManualUrl(e.target.value)}
              placeholder="https://..."
            />

            <button type="button" css={button} disabled={!manualUrl} onClick={handleManualUrl}>
              Usar esta URL
            </button>
          </div>
        </div>
      )}

      {value && (
        <div css={previewCard}>
          <div css={preview}>
            {isImage && <img src={value} alt="media-preview" />}
            {isVideo && <video src={value} controls />}
            {!isImage && !isVideo && <span>Archivo seleccionado</span>}
          </div>

          <div css={urlBox}>
            <strong>URL actual:</strong>
            <br />
            {value}
            <br />
            <br />
            <button type="button" css={button} onClick={handleCopy}>
              Copiar URL
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MediaField;